import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Plus } from "lucide-react";
import { TaskForm } from "./TaskForm";
import { TaskList, TaskShape } from "./TaskList";

type Props = {
  scope: { contact_id?: string; organization_id?: string; opportunity_id?: string };
};

export const TaskPanels: React.FC<Props> = ({ scope }) => {
  const { user } = useAuth();
  const [items, setItems] = useState<TaskShape[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const [addFollowUpOpen, setAddFollowUpOpen] = useState<boolean>(false);
  const canMutate = user?.tenant_role !== "viewer";

  const refetch = useCallback(async () => {
    setError("");
    const params: Record<string, string> = { limit: "200", sort: "due" };
    if (scope.contact_id) params.contact_id = scope.contact_id;
    if (scope.organization_id) params.organization_id = scope.organization_id;
    if (scope.opportunity_id) params.opportunity_id = scope.opportunity_id;
    try {
      const r = await api.get("/crm/tasks", { params });
      setItems(r.data.items || []);
    } catch {
      setError("Could not load tasks.");
    } finally {
      setLoading(false);
    }
  }, [scope.contact_id, scope.organization_id, scope.opportunity_id]);

  useEffect(() => { refetch(); }, [refetch]);

  const openTasks = items.filter(
    (t) => t.status === "open" || t.status === "in_progress",
  );
  const nextFollowUp = openTasks.find(
    (t) => t.task_type_code === "follow_up"
       || t.task_type_code.startsWith("follow_up"),
  );
  const historyTasks = items.filter(
    (t) => t.status === "completed" || t.status === "cancelled",
  );

  return (
    <section className="border border-slate-200 rounded-lg bg-white p-4 md:p-5"
             data-testid="record-task-panels">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Manrope" }}>
          Tasks & Follow-Ups
        </h3>
        {canMutate ? (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}
              data-testid="record-task-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Task
            </Button>
            <Button size="sm" onClick={() => setAddFollowUpOpen(true)}
              data-testid="record-task-add-followup-btn">
              <Plus className="w-4 h-4 mr-1" /> Follow-Up
            </Button>
          </div>
        ) : null}
      </div>
      <div className="mt-4 space-y-4">
        {loading ? (
          <div className="text-sm text-slate-500" role="status" aria-busy="true"
            data-testid="record-tasks-loading">Loading tasks…</div>
        ) : error ? (
          <div role="alert" className="text-sm text-red-600"
            data-testid="record-tasks-error">{error}</div>
        ) : (
          <>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Next follow-up
              </div>
              {nextFollowUp ? (
                <TaskList tasks={[nextFollowUp]} onRefetch={refetch}
                  testId="record-next-followup" readOnly={!canMutate} />
              ) : (
                <div className="text-sm text-slate-500" data-testid="record-next-followup-empty">
                  No follow-up scheduled.
                </div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                Open tasks
              </div>
              <TaskList tasks={openTasks} onRefetch={refetch}
                testId="record-open-tasks" readOnly={!canMutate}
                emptyText="No open tasks." />
            </div>
            <div>
              <button className="text-xs font-medium text-slate-500 uppercase tracking-wide"
                onClick={() => setShowHistory((v) => !v)}
                data-testid="record-tasks-history-toggle">
                {showHistory ? "Hide" : "Show"} completed/cancelled ({historyTasks.length})
              </button>
              {showHistory ? (
                <div className="mt-2">
                  <TaskList tasks={historyTasks} onRefetch={refetch}
                    testId="record-history-tasks" readOnly
                    emptyText="No completed or cancelled tasks yet." />
                </div>
              ) : null}
            </div>
          </>
        )}
      </div>

      {canMutate && user ? (
        <TaskForm open={addOpen} onOpenChange={setAddOpen}
          onSuccess={() => refetch()}
          scope={scope}
          currentUserId={user.id} />
      ) : null}
      {canMutate && user ? (
        <TaskForm open={addFollowUpOpen} onOpenChange={setAddFollowUpOpen}
          onSuccess={() => refetch()}
          scope={scope}
          presetFollowUp
          currentUserId={user.id} />
      ) : null}
    </section>
  );
};
